import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { resolveLandingSurface } from 'src/utils/marketing/landingSurface'
import { isInternalTraffic } from 'src/utils/pixels/internal'
import posthog from 'src/utils/pixels/posthog'

/**
 * Bounce-vs-engaged signal for demand-side landing pages.
 *
 * PostHog's autocaptured `$pageview` tells us a page loaded — it does NOT
 * tell us whether the user looked at it. A 2-second bounce and a 90-second
 * read both produce exactly one `$pageview`. To compute a real bounce rate
 * by surface (paid `?city=monterrey` vs bare pillar vs city route vs hood
 * route) we need a second event that fires only when the user signals
 * actual engagement.
 *
 * Engagement = any of:
 *   - scroll past 25% of the document
 *   - 15 s elapsed on page
 *   - any click, keydown, or touchstart
 *
 * Fires `landing_engaged` at most once per (session, surface). Cleanup
 * tears down listeners on route change so we don't accumulate stale
 * handlers across SPA navigations.
 *
 * Bounce rate per surface =
 *   1 - (sessions with `landing_engaged` for surface S
 *        / sessions with `$pageview` on surface S)
 *
 * Skipped surfaces:
 *  - `flow` / `dashboard` — post-intent, not a landing
 *  - `listing_short` / `listing_lodging` — redirect or own event flow
 *  - `unknown` — only if surface resolver bails
 *
 * Must be mounted INSIDE Next.js router context. Sits next to
 * `<UTMs />` and `<LandingViewTracker />` in `_app.tsx`.
 */

const SKIP_SURFACES = new Set([
  'flow',
  'dashboard',
  'listing_short',
  'listing_lodging',
  'unknown'
])

const ENGAGEMENT_DELAY_MS = 15_000
const SCROLL_THRESHOLD = 0.25

const sessionKey = (surface: string): string => `landing_engaged:${surface}`

const alreadyFiredThisSession = (surface: string): boolean => {
  if (typeof window === 'undefined') return false
  try {
    return window.sessionStorage.getItem(sessionKey(surface)) !== null
  } catch {
    return false
  }
}

const markFiredThisSession = (surface: string): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(sessionKey(surface), '1')
  } catch {
    // sessionStorage unavailable — fall through; the in-effect ref still
    // prevents duplicates within a single route lifecycle.
  }
}

const LandingEngagementTracker: React.FC = () => {
  const router = useRouter()

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (isInternalTraffic()) return

    const surface = resolveLandingSurface(router.asPath)
    if (SKIP_SURFACES.has(surface)) return
    if (alreadyFiredThisSession(surface)) return

    let fired = false
    let timer: number | undefined

    const onScroll = (): void => {
      const scrolled = window.scrollY + window.innerHeight
      const total = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight
      )
      if (total <= 0) return
      if (scrolled / total >= SCROLL_THRESHOLD) fire('scroll')
    }

    const onInteraction = (): void => fire('interaction')

    const cleanup = (): void => {
      if (timer !== undefined) window.clearTimeout(timer)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('click', onInteraction)
      window.removeEventListener('keydown', onInteraction)
      window.removeEventListener('touchstart', onInteraction)
    }

    const fire = (trigger: 'scroll' | 'timer' | 'interaction'): void => {
      if (fired) return
      fired = true
      markFiredThisSession(surface)
      posthog.event('landing_engaged', {
        landing_surface: surface,
        page_path: router.asPath.split('?')[0],
        trigger
      })
      cleanup()
    }

    timer = window.setTimeout(() => fire('timer'), ENGAGEMENT_DELAY_MS)
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('click', onInteraction, { passive: true })
    window.addEventListener('keydown', onInteraction, { passive: true })
    window.addEventListener('touchstart', onInteraction, { passive: true })

    return cleanup
  }, [router.asPath])

  return null
}

export default LandingEngagementTracker
